/**
 * The v3 "try it" console — the /api page's v3 slot.
 *
 * Everything here is read from the live contract (`contract.ts`) and sent for real: there
 * is no simulated mode, no mock responses, no local fixture data. That is the point of a
 * console like this (an integrator needs to see the actual API behave), and it is also
 * the risk the brief calls out explicitly — a "VENDOR" endpoint spends real money with the
 * screening/verification provider behind it, so those calls get a confirmation step
 * (`Confirm`) that a plain GET does not.
 *
 * Layout: a searchable, grouped endpoint list on the left; the selected endpoint's
 * parameters/body form, the literal request it builds, and the response, on the right.
 * Credentials live in a drawer (`Drawer`/`DrawerHead`, the shared primitive) reachable
 * from the top bar, because the minted token is reused across whatever endpoint is
 * selected next.
 */
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { Drawer, DrawerHead, Section, Confirm, ThemeToggle } from 'views/design-system/components';

import {
	Operation,
	OpenAPIDoc,
	fetchContract,
	listOperations,
	groupOperations,
	operationMatches,
	paramKey,
	trimSlash,
} from './contract';
import { resolveSchema, defaultFor, SchemaField, BodyEditor } from './schema-form';
import {
	buildRequest,
	missingRequiredParams,
	SNIPPET_LANGUAGES,
	SnippetLanguage,
} from './request';
import { JsonView, RawTextView } from './json-view';
import { ActiveKey, AuthPanel, KeyMode, TokenInfo } from './auth-panel';
import './api-console.scss';

const BASE_URL_KEY = 'simplici-api-console-base-url';

/** The gateway's own shape (`{host}/v1`) swapped for `/v3` — v3 is one adapter behind the
 *  same local gateway, and the gateway is what already stamps CORS headers for any
 *  origin (checked: api.v3 itself sends none). Falls back to the gateway's default local
 *  port only if the panel's own API_HOST env is somehow unset. */
const defaultBaseUrl = () => {
	if (API_HOST) return API_HOST.replace(/\/v1\/?$/, '/v3');
	return 'http://localhost:8086/v3';
};

const storedBaseUrl = () => {
	try {
		return localStorage.getItem(BASE_URL_KEY) || defaultBaseUrl();
	} catch {
		return defaultBaseUrl();
	}
};

const METHOD_TONE: Record<string, string> = {
	GET: 'strt',
	POST: 'ok',
	PUT: 'rev',
	PATCH: 'rev',
	DELETE: 'rej',
};

const uuid = () => {
	try {
		// Not in this project's `lib` TS target yet, though every browser we run in has it.
		const c = crypto as Crypto & { randomUUID?: () => string };
		if (typeof c.randomUUID === 'function') return c.randomUUID();
	} catch {
		/* fall through to the manual id below */
	}
	return `idem_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

/** Seeds a fresh operation's param values: an Idempotency-Key gets a real generated
 *  default (it is required on billable/write calls and a developer trying the console
 *  should not be blocked typing one), everything else starts empty/omitted. */
const initialParamValues = (op: Operation): Record<string, any> => {
	const values: Record<string, any> = {};
	for (const p of op.parameters) {
		if (p.name === 'Idempotency-Key') values[paramKey(p)] = uuid();
		else if (p.schema?.default !== undefined) values[paramKey(p)] = p.schema.default;
	}
	return values;
};

interface ResponseState {
	status: number;
	statusText: string;
	duration: number;
	headers: Record<string, string>;
	text: string;
	json: unknown;
	isJson: boolean;
	requestId: string | null;
}

/**
 * A BEARER TOKEN IS ~230 CHARACTERS AND IT WAS BEING PRINTED RAW.
 *
 * In the request preview and in every language snippet, the Authorization value
 * ran off the right edge of the screen and dragged the layout with it — and the
 * snippet, whose entire job is to show the SHAPE of the call, became a scrolled
 * slab of base64. Masked to its first and last few characters, with one toggle
 * that reveals the real value in place, the same pattern the credentials drawer
 * already uses for a client secret.
 */
const maskToken = (text: string): string =>
	text.replace(/(Bearer\s+)([A-Za-z0-9._-]{24,})/g, (_m, pre: string, tok: string) =>
		`${pre}${tok.slice(0, 12)}${'\u2022'.repeat(22)}${tok.slice(-8)}`
	);

/** A client id is public and identifying, so it is shortened to fit a badge —
 *  never masked. The full value is on the request strip and in the drawer. */
const shortKey = (id: string): string =>
	id.length > 16 ? `${id.slice(0, 8)}\u2026${id.slice(-4)}` : id;

/**
 * Parameters in labelled groups rather than one flat list.
 *
 * A 17-parameter endpoint as a single column of fields is a wall nobody reads.
 * The grouping is by MEANING, matched on names the contract actually uses; an
 * endpoint whose parameters match nothing keeps one unlabelled group, so a
 * heuristic that does not recognise something never hides it.
 */
const PARAM_GROUPS: {title: string; test: (n: string) => boolean}[] = [
	{title: 'Pagination & sort', test: n => /^(limit|cursor|sort|page|page_size|order)$/.test(n)},
	{title: 'Status & risk', test: n => /status|review_bucket|risk|decision|state/.test(n)},
	{title: 'Date range', test: n => /_at(\[|$)|^(from|to|since|until|cycle)$/.test(n)},
	{title: 'Find', test: n => /email|phone|search|name|code|_id$|^id$/.test(n)},
];

const groupParams = <T extends {name: string}>(list: T[]): {title: string | null; items: T[]}[] => {
	if (list.length < 6) return [{title: null, items: list}];
	const out: {title: string | null; items: T[]}[] = [];
	const taken = new Set<T>();
	for (const g of PARAM_GROUPS) {
		const items = list.filter(p => !taken.has(p) && g.test(p.name.toLowerCase()));
		items.forEach(i => taken.add(i));
		if (items.length) out.push({title: g.title, items});
	}
	const rest = list.filter(p => !taken.has(p));
	if (rest.length) out.push({title: out.length ? 'Other' : null, items: rest});
	return out;
};

/**
 * Advice per error code — the general case, in the product's own words.
 *
 * The SERVER's `fix` always wins when it sends one: it knows things this table
 * cannot, such as whether the credential grants a scope the token is missing.
 * This is the fallback, and it covers the failures a console actually produces.
 */
const FIX_BY_CODE: Record<string, string> = {
	missing_token:
		'No bearer token was sent. Open Credentials, choose sandbox or production keys, and Generate token — it is attached to every request from then on.',
	invalid_token:
		'The token was not accepted. It may have expired (they last an hour) — generate a new one in Credentials.',
	token_expired: 'Tokens last an hour. Generate a new one in Credentials.',
	credential_revoked:
		'The credential behind this token was revoked. Use a different key, or create one in Credentials.',
	invalid_client:
		'That client id and secret are not a pair. Check both in Credentials — a sandbox secret cannot mint a production token, or the reverse.',
	resource_missing:
		'Nothing of yours has that id. Ids are scoped to your business, so one copied from another environment or another tenant reads as missing rather than forbidden.',
	rate_limited:
		'Too many requests in this minute. Wait for the window to reset — the RateLimit-Reset header says when.',
	invalid_cursor:
		'That cursor was not issued by us, or has expired (24h). Start the list again with no cursor.',
	not_implemented_yet:
		'This endpoint is in the contract but not built in this deployment yet. GET /v3 lists what is actually served here.',
	upstream_unavailable:
		'The product service behind this endpoint did not answer. If you are running locally, that service may be down — check make status.',
	validation_error:
		'The request body did not match the contract. The `param` above is a JSON Pointer to the field at fault.',
	idempotency_conflict:
		'This Idempotency-Key was already used with a different body. Use a new key, or resend the identical request.',
};

/**
 * The fix-it frame.
 *
 * It offers the ACTION where there is one. The scope case is the reason this
 * exists: a credential that was never granted a scope cannot mint a token that
 * has it, so no amount of retrying the request helps — and the developer has no
 * way of knowing that from the 403 alone.
 */
const FixIt: FC<{
	response: {status: number; json: any; text: string; isJson: boolean};
	baseUrl: string;
	hasToken: boolean;
	onOpenCredentials: () => void;
	onWidened: () => void;
}> = ({ response, baseUrl, hasToken, onOpenCredentials, onWidened }) => {
	const [busy, setBusy] = useState(false);
	const [done, setDone] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);

	const error = response.isJson ? response.json?.error : null;
	const code = String(error?.code ?? '');
	const details = error?.details ?? null;
	const advice: string =
		error?.fix ||
		FIX_BY_CODE[code] ||
		(response.status >= 500
			? 'Something failed on our side. Quote the request_id above — it is what support needs to find this exact call.'
			: 'Check the request against the endpoint\'s parameters on the left. The message above says what was refused.');

	/* Only when the credential itself is too narrow — the one case a developer
	   cannot fix from the request side at all — and never for the scope that
	   could mint a production key. */
	const canWiden =
		code === 'insufficient_scope' &&
		details &&
		details.credential_grants_it === false &&
		!(details.missing_scopes ?? []).includes('credentials:manage');

	const widen = async () => {
		setBusy(true);
		setErr(null);
		try {
			const raw = document.cookie
				.split('; ')
				.find(c => c.startsWith('user='))
				?.slice('user='.length);
			const session = raw ? JSON.parse(decodeURIComponent(raw))?.token : null;
			if (!session) throw new Error('Sign in to the panel first.');
			const res = await fetch(`${trimSlash(baseUrl)}/console/sandbox_credential/scopes`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', authorization: `Bearer ${session}` },
			});
			const body = await res.json().catch(() => null);
			const d = body?.data ?? body;
			if (!res.ok) throw new Error(d?.error?.message || `Could not widen the key (${res.status}).`);
			setDone(
				(d.added ?? []).length
					? `Added ${(d.added ?? []).join(', ')}. Your old token still carries the old scopes — generate a new one in Credentials, then send again.`
					: 'That key already had these scopes. If the call still fails, the token predates them — generate a new one.'
			);
			/* The current token is now stale in a way that matters, so it is dropped
			   rather than left to fail the same way twice. */
			onWidened();
		} catch (e: any) {
			setErr(e?.message || 'Could not widen the key.');
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="fixit">
			<p className="fixit-advice">{advice}</p>

			{code === 'insufficient_scope' && details ? (
				<div className="fixit-scopes">
					<div>
						<span className="dflabel">Needs</span>
						<span className="scope-chips">
							{(details.missing_scopes ?? []).map((sc: string) => (
								<span className="scope-chip missing" key={sc}>
									{sc}
								</span>
							))}
						</span>
					</div>
					<div>
						<span className="dflabel">Your token has</span>
						<span className="scope-chips">
							{(details.token_scopes ?? []).length ? (
								(details.token_scopes ?? []).map((sc: string) => (
									<span className="scope-chip" key={sc}>
										{sc}
									</span>
								))
							) : (
								<span className="chint">none</span>
							)}
						</span>
					</div>
				</div>
			) : null}

			<div className="fixit-actions">
				{canWiden ? (
					<button type="button" className="btn pri" disabled={busy} onClick={widen}>
						{busy ? 'Updating…' : 'Widen my sandbox key (all reads + webhooks)'}
					</button>
				) : null}
				{!hasToken || code === 'missing_token' || code === 'invalid_token' || code === 'token_expired' ? (
					<button type="button" className="btn pri" onClick={onOpenCredentials}>
						Open Credentials
					</button>
				) : code === 'insufficient_scope' ? (
					<button type="button" className="btn" onClick={onOpenCredentials}>
						Open Credentials
					</button>
				) : null}
			</div>
			{done ? <div className="fixit-done">{done}</div> : null}
			{err ? <div className="dferr">{err}</div> : null}
		</div>
	);
};

const ENV_LABEL: Record<string, string> = {
	live: 'PRODUCTION KEYS',
	sandbox: 'SANDBOX KEYS',
	'live-pending': 'PRODUCTION · no token yet',
	'sandbox-pending': 'SANDBOX · no token yet',
};
const ENV_TITLE: Record<string, string> = {
	live: 'Every call is a real check on a real person, and is billed.',
	sandbox: 'Test keys: nothing is billed and no real applicant is touched.',
	'live-pending':
		'Production is selected, but no token has been minted — paste a production secret in Credentials.',
	'sandbox-pending': 'Sandbox is selected. Mint a token in Credentials to start sending.',
};

export const ApiConsole = () => {
	const [baseUrl, setBaseUrl] = useState(storedBaseUrl);
	const [baseUrlDraft, setBaseUrlDraft] = useState(baseUrl);
	const [doc, setDoc] = useState<OpenAPIDoc | null>(null);
	const [loadErr, setLoadErr] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const [query, setQuery] = useState('');
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const [paramValuesById, setParamValuesById] = useState<Record<string, Record<string, any>>>({});
	const [bodyValuesById, setBodyValuesById] = useState<Record<string, any>>({});

	const [token, setToken] = useState<TokenInfo | null>(null);
	const [authOpen, setAuthOpen] = useState(false);

	const [confirmOp, setConfirmOp] = useState<Operation | null>(null);
	const [sending, setSending] = useState(false);
	const [keyMode, setKeyMode] = useState<KeyMode>('sandbox');
	/* WHICH CREDENTIAL, not only which environment. Set in the drawer by "Use
	   these keys"; shown on the request, because that is where it decides
	   something. */
	const [activeKey, setActiveKey] = useState<ActiveKey | null>(null);
	/* Per-tab only, and off by default: the token is real and this page is often
	   on a screen somebody else is looking at. */
	const [showToken, setShowToken] = useState(false);
	const [copied, setCopied] = useState<string | null>(null);
	const [sendErr, setSendErr] = useState<string | null>(null);
	const [response, setResponse] = useState<ResponseState | null>(null);
	const [snippetTab, setSnippetTab] = useState<SnippetLanguage>('curl');

	const load = useCallback(async (url: string) => {
		setLoading(true);
		setLoadErr(null);
		try {
			const d = await fetchContract(url);
			setDoc(d);
		} catch (e: any) {
			setDoc(null);
			setLoadErr(
				e?.message ||
					'Could not load the contract. Check the base URL, and that it allows this origin (CORS) — see _flows/v3-api-console.md.'
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load(baseUrl);
	}, [baseUrl, load]);

	const applyBaseUrl = () => {
		const next = trimSlash(baseUrlDraft.trim());
		if (!next) return;
		setBaseUrl(next);
		try {
			localStorage.setItem(BASE_URL_KEY, next);
		} catch {
			/* non-fatal — the base URL is just a convenience, not a secret */
		}
	};

	const resetBaseUrl = () => {
		const d = defaultBaseUrl();
		setBaseUrlDraft(d);
		setBaseUrl(d);
		try {
			localStorage.removeItem(BASE_URL_KEY);
		} catch {
			/* ignore */
		}
	};

	const ops = useMemo(() => (doc ? listOperations(doc) : []), [doc]);
	const groups = useMemo(() => (doc ? groupOperations(doc, ops) : []), [doc, ops]);
	const selected = useMemo(() => ops.find(o => o.id === selectedId) || null, [ops, selectedId]);

	useEffect(() => {
		if (!selected) return;
		setParamValuesById(prev =>
			prev[selected.id] ? prev : { ...prev, [selected.id]: initialParamValues(selected) }
		);
		setBodyValuesById(prev => {
			if (!selected.requestBody || prev[selected.id] !== undefined) return prev;
			const resolved = doc ? resolveSchema(doc, selected.requestBody.schema) : {};
			return { ...prev, [selected.id]: defaultFor(resolved) };
		});
		setResponse(null);
		setSendErr(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected?.id]);

	const paramValues = (selected && paramValuesById[selected.id]) || {};
	const bodyValue = selected ? bodyValuesById[selected.id] : undefined;

	const setParam = (key: string, v: any) => {
		if (!selected) return;
		setParamValuesById(prev => ({ ...prev, [selected.id]: { ...prev[selected.id], [key]: v } }));
	};
	const setBody = (v: any) => {
		if (!selected) return;
		setBodyValuesById(prev => ({ ...prev, [selected.id]: v }));
	};

	const built = useMemo(() => {
		if (!selected) return null;
		return buildRequest({ baseUrl, op: selected, paramValues, bodyValue, token: token?.value });
	}, [selected, baseUrl, paramValues, bodyValue, token]);

	const runSend = async (op: Operation) => {
		if (!built) return;
		setSending(true);
		setSendErr(null);
		setResponse(null);
		const start = performance.now();
		try {
			const res = await fetch(built.url, {
				method: built.method,
				headers: built.headers,
				body: built.bodyText,
			});
			const duration = performance.now() - start;
			const headers: Record<string, string> = {};
			res.headers.forEach((v, k) => {
				headers[k] = v;
			});
			const text = await res.text();
			let json: unknown;
			let isJson = false;
			try {
				json = JSON.parse(text);
				isJson = true;
			} catch {
				/* not JSON — shown as raw text */
			}
			setResponse({
				status: res.status,
				statusText: res.statusText,
				duration,
				headers,
				text,
				json,
				isJson,
				requestId: headers['simplici-request-id'] || null,
			});
		} catch (e: any) {
			setSendErr(
				e?.message ||
					'The request failed before a response came back — usually CORS (the target must allow this origin) or nothing is listening at that base URL.'
			);
		} finally {
			setSending(false);
		}
	};

	const requestSend = () => {
		if (!selected) return;
		const missing = missingRequiredParams(selected, paramValues);
		if (missing.length) {
			setSendErr(`Required and empty: ${missing.map(p => p.name).join(', ')}`);
			return;
		}
		if (selected.rateClass === 'vendor') {
			setConfirmOp(selected);
			return;
		}
		runSend(selected);
	};

	const statusTone = (status: number) => (status < 300 ? 'ok' : status < 500 ? 'rev' : 'rej');

	/**
	 * The environment shown on the page.
	 *
	 * A live token's own word for itself beats everything — it comes from
	 * `/oauth/introspect`, and the credential decides, not the person. With no
	 * token yet the badge reports the CHOICE, and says plainly that it is only a
	 * choice: "production · no token yet" is useful, while a bare "SANDBOX" on a
	 * page holding no credential would be the false reassurance this exists to
	 * prevent.
	 */
	const envMode: 'live' | 'sandbox' | 'none' = token?.environment
		? token.environment === 'live'
			? 'live'
			: 'sandbox'
		: 'none';
	const envBadge = envMode !== 'none' ? envMode : keyMode === 'production' ? 'live-pending' : 'sandbox-pending';
	const isLive = envMode === 'live' || (envMode === 'none' && keyMode === 'production');

	return (
		<div className="sv2-scope sv2 api-console-deck">
			{/* The warning was one ~200-character sentence stretched across the full
			    page width, with `display:flex` turning "Live API." and the VENDOR chip
			    into flex ITEMS — so the label wrapped into its own narrow column and
			    the chip broke the sentence in half mid-clause. A caution nobody can
			    read in one pass is not a caution. Same words, said in three short
			    lines at a readable measure, with the chip LABELLING its own clause
			    instead of interrupting another one. */}
			<div className={`ac-banner env-${envMode === 'none' && keyMode === 'production' ? 'live' : envMode}`}>
				<span className="ac-banner-dot" aria-hidden="true" />
				<div className="ac-banner-body">
					<div className="ac-banner-title">
						{envMode === 'live' || (envMode === 'none' && keyMode === 'production')
							? 'You are using PRODUCTION keys'
							: envMode === 'sandbox'
								? 'Sandbox keys — but a live stack'
								: 'Live API — not a sandbox'}
					</div>
					<div className="ac-banner-lines">
						<p>
							{envMode === 'live' || (envMode === 'none' && keyMode === 'production')
								? 'Every call runs a real check on a real person and is billed. Nothing here is simulated.'
								: envMode === 'sandbox'
									? 'Your token is a sandbox one, so vendor checks are not billed — but every call still reaches the running api.v3 stack and reads or writes real data in this environment.'
									: 'Every call reaches the running api.v3 stack. Nothing here is simulated: a request writes or reads real data the moment you press Send.'}
						</p>
						<p className="ac-banner-vendor">
							<span className="vch rej">VENDOR</span>
							<span>
								endpoints also spend money with the screening provider behind them. Those ask for
								confirmation first.
							</span>
						</p>
					</div>
				</div>
			</div>

			<div className="ac-topbar">
				<div className="base-url">
					<span className="dflabel">Base URL</span>
					<input
						className="mono"
						value={baseUrlDraft}
						onChange={e => setBaseUrlDraft(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && applyBaseUrl()}
						spellCheck={false}
					/>
					<button type="button" className="btn" onClick={applyBaseUrl}>
						Apply
					</button>
					<button type="button" className="btn" onClick={resetBaseUrl}>
						Reset
					</button>
				</div>
				{/* WHICH KEYS AM I ON — on the page itself, not only inside the drawer.
				    Taken from the TOKEN (via /oauth/introspect) when one exists, because
				    the credential decides and a person can believe otherwise; only when
				    there is no token does it fall back to the saved preference. */}
				<button
					type="button"
					className={`env-badge ${envBadge}`}
					title={`${ENV_TITLE[envBadge]} Click to open Credentials.`}
					onClick={() => setAuthOpen(true)}
				>
					<span className="env-dot" aria-hidden="true" />
					<span className="env-text">{ENV_LABEL[envBadge]}</span>
					{/* THE CREDENTIAL ITSELF, not only its environment. Two production
					    keys with different scopes fail in different ways, and a badge
					    reading PRODUCTION does not say which one is on the wire. */}
					{activeKey ? <span className="env-key mono">{shortKey(activeKey.clientId)}</span> : null}
				</button>
				<button type="button" className={`btn pri auth-trigger${token ? ' has-token' : ''}`} onClick={() => setAuthOpen(true)}>
					{token ? 'Credentials · token active' : 'Credentials'}
				</button>
				<ThemeToggle />
			</div>

			{loading ? <div className="ac-note">Loading the contract…</div> : null}
			{loadErr ? <div className="ac-note ac-note--err">{loadErr}</div> : null}

			{doc ? (
				<div className="ac-layout">
					<div className="ac-nav">
						<label className="srch">
							<svg viewBox="0 0 24 24">
								<circle cx="11" cy="11" r="7" />
								<line x1="16.5" y1="16.5" x2="21" y2="21" />
							</svg>
							<input
								value={query}
								placeholder="Search endpoints"
								onChange={e => setQuery(e.target.value)}
							/>
						</label>
						<div className="op-tree">
							{groups.map(group => {
								const visibleTags = group.tags
									.map(tag => ({ ...tag, ops: tag.ops.filter(o => operationMatches(o, query)) }))
									.filter(tag => tag.ops.length);
								if (!visibleTags.length) return null;
								return (
									<div className="op-group" key={group.name}>
										<div className="grp">{group.name}</div>
										{visibleTags.map(tag => (
											<details className="op-tag" key={tag.name} open>
												<summary>
													{tag.name}
													<span className="fcount">{tag.ops.length}</span>
												</summary>
												{tag.ops.map(op => (
													<button
														type="button"
														key={op.id}
														className={`op-row${op.id === selectedId ? ' on' : ''}`}
														onClick={() => setSelectedId(op.id)}
													>
														<span className={`vch ${METHOD_TONE[op.method] || 'ns'}`}>{op.method}</span>
														<span className="op-path mono">{op.path}</span>
														{op.rateClass === 'vendor' ? <span className="vch rej op-vendor">$</span> : null}
													</button>
												))}
											</details>
										))}
									</div>
								);
							})}
						</div>
					</div>

					<div className="ac-main">
						{!selected ? (
							<div className="blank">
								<div className="bti">Pick an endpoint</div>
								<div className="bds">
									{ops.length} endpoints, read live from {trimSlash(baseUrl)}/openapi.json. Search or
									browse the groups on the left.
								</div>
							</div>
						) : (
							<>
								<div className="op-head">
									<span className={`vch ${METHOD_TONE[selected.method] || 'ns'}`}>{selected.method}</span>
									<span className="op-path mono">{selected.path}</span>
									{selected.rateClass === 'vendor' ? (
										<span className="vch rej">VENDOR — billable</span>
									) : null}
									{selected.anonymous ? <span className="vch ns">no auth</span> : null}
								</div>
								<div className="op-summary">{selected.summary}</div>
								{selected.description ? <div className="op-desc">{selected.description}</div> : null}
								{selected.scopes.length ? (
									<div className="scope-chips">
										{selected.scopes.map(s => (
											<span className="scope-chip" key={s}>
												{s}
											</span>
										))}
									</div>
								) : null}

								<div className="ac-columns">
									<div className="ac-col">
										{selected.parameters.length ? (
											<Section
												title="Parameters"
												right={
													<span className="chint mono">
														{
															selected.parameters.filter(
																p =>
																	paramValues[paramKey(p)] !== undefined &&
																	paramValues[paramKey(p)] !== '' &&
																	paramValues[paramKey(p)] !== null
															).length
														}{' '}
														of {selected.parameters.length} set
													</span>
												}
											>
												<div className="param-form">
													{(['path', 'query', 'header'] as const).map(loc => {
														const list = selected.parameters.filter(p => p.in === loc);
														if (!list.length) return null;
														return (
															<div className="param-group" key={loc}>
																{/* `path` and `header` are short and self-evident; only the
																    query list is long enough to need breaking up. */}
																{loc === 'query' ? null : <div className="grp">{loc}</div>}
																{(loc === 'query' ? groupParams(list) : [{title: null, items: list}]).map(
																	(g, gi) => (
																		<div className="param-sub" key={g.title ?? `g${gi}`}>
																			{g.title ? <div className="grp">{g.title}</div> : null}
																			{g.items.map(p => (
																				<SchemaField
																					key={paramKey(p)}
																					doc={doc}
																					schema={{ ...p.schema, description: p.description ?? p.schema?.description }}
																					value={paramValues[paramKey(p)]}
																					onChange={v => setParam(paramKey(p), v)}
																					name={p.name}
																					required={p.required}
																					depth={1}
																				/>
																			))}
																		</div>
																	)
																)}
															</div>
														);
													})}
												</div>
											</Section>
										) : null}

										{selected.requestBody ? (
											<Section
												title="Request body"
												right={
													selected.requestBody.alsoAccepts.length ? (
														<span className="chint">
															also accepts {selected.requestBody.alsoAccepts.join(', ')} — sent as JSON here
														</span>
													) : undefined
												}
											>
												<BodyEditor
													doc={doc}
													schema={selected.requestBody.schema}
													value={bodyValue}
													onChange={setBody}
												/>
											</Section>
										) : null}
									</div>

									<div className="ac-col ac-col--response">
										<Section title="Request">
											<div className="req-preview">
												<div className="req-line mono">
													<span className={`vch ${METHOD_TONE[built?.method || 'GET'] || 'ns'}`}>
														{built?.method}
													</span>
													<span className="req-url">{built?.url}</span>
													<button
														type="button"
														className="btn copy-url"
														onClick={async () => {
															try {
																await navigator.clipboard.writeText(built?.url ?? '');
																setCopied('url');
																setTimeout(() => setCopied(null), 1500);
															} catch {
																/* a failed copy is not worth an error state here */
															}
														}}
													>
														{copied === 'url' ? 'Copied' : 'Copy URL'}
													</button>
												</div>
												{/* Collapsed: a developer usually already knows what they sent, and
												    the Authorization row was the thing pushing this page wide. */}
												<details className="req-headers">
													<summary>
														Headers ({Object.keys(built?.headers || {}).length})
														{Object.keys(built?.headers || {}).some(k => /authorization/i.test(k)) ? (
															<span
																className="tok-reveal"
																role="button"
																tabIndex={0}
																onClick={e => {
																	e.preventDefault();
																	setShowToken(v => !v);
																}}
																onKeyDown={e => {
																	if (e.key === 'Enter' || e.key === ' ') {
																		e.preventDefault();
																		setShowToken(v => !v);
																	}
																}}
															>
																{showToken ? 'hide token' : 'show token'}
															</span>
														) : null}
													</summary>
													<table className="kv">
														<tbody>
															{Object.entries(built?.headers || {}).map(([k, v]) => (
																<tr key={k}>
																	<td>{k}</td>
																	<td className="mono">{showToken ? v : maskToken(String(v))}</td>
																</tr>
															))}
														</tbody>
													</table>
												</details>
												{built?.bodyText !== undefined ? (
													<pre className="mono req-body">{built.bodyText}</pre>
												) : null}
											</div>

											{/* One tab per language, from SNIPPET_LANGUAGES — adding a
											    language there adds it here, with nothing to keep in step. */}
											<div className="snippet">
												<div className="snippet-bar">
												<div className="ktabs snippet-langs">
													{SNIPPET_LANGUAGES.map(l => (
														<span
															key={l.key}
															className={`ktab${snippetTab === l.key ? ' on' : ''}`}
															onClick={() => setSnippetTab(l.key)}
														>
															{l.label}
														</span>
													))}
												</div>
												<button
													type="button"
													className="btn snippet-copy"
													onClick={async () => {
														const code = built
															? (
																	SNIPPET_LANGUAGES.find(l => l.key === snippetTab) ??
																	SNIPPET_LANGUAGES[0]
																).render(built)
															: '';
														try {
															/* The REAL token, never the masked form: a snippet copied
															   with bullets in it is a snippet that does not run. */
															await navigator.clipboard.writeText(code);
															setCopied('snippet');
															setTimeout(() => setCopied(null), 1500);
														} catch {
															/* see above */
														}
													}}
												>
													{copied === 'snippet' ? 'Copied' : 'Copy'}
												</button>
												</div>
												<pre className="mono snippet-code">
													{(() => {
														const code = built
															? (
																	SNIPPET_LANGUAGES.find(l => l.key === snippetTab) ??
																	SNIPPET_LANGUAGES[0]
																).render(built)
															: '';
														return showToken ? code : maskToken(code);
													})()}
												</pre>
												{/* Only when there IS a token to mask: with no credentials the
												    snippet carries no Authorization header, and explaining that
												    nothing is hidden is worse than saying nothing. */}
												{!showToken &&
												built &&
												Object.keys(built.headers || {}).some(k => /authorization/i.test(k)) ? (
													<div className="snippet-hint">
														Token masked. &ldquo;show token&rdquo; above reveals it; Copy always copies the
														real one.
													</div>
												) : null}
											</div>

											{/* WHICH KEYS THIS SEND WILL USE, on the request, where the
											    decision is. The topbar badge is easy to stop seeing; this
											    sits directly above the button that spends the money, names
											    the credential rather than only its environment, and is
											    loudest when there is no credential at all. */}
											<div
												className={`using-keys ${
													activeKey ? activeKey.environment : token ? envMode : 'none'
												}`}
											>
												<span className="uk-dot" aria-hidden="true" />
												<div className="uk-body">
													{activeKey ? (
														<>
															<div className="uk-line">
																<span className="uk-env">
																	{activeKey.environment === 'live' ? 'PRODUCTION' : 'SANDBOX'}
																</span>
																<span className="uk-label">{activeKey.label}</span>
															</div>
															<div className="uk-id mono">{activeKey.clientId}</div>
														</>
													) : token ? (
														<>
															<div className="uk-line">
																<span className="uk-env">
																	{envMode === 'live'
																		? 'PRODUCTION'
																		: envMode === 'sandbox'
																			? 'SANDBOX'
																			: 'ENVIRONMENT UNKNOWN'}
																</span>
																<span className="uk-label">token from a pasted credential</span>
															</div>
														</>
													) : (
														<div className="uk-line">
															<span className="uk-label">
																No keys in use — this request is sent unauthenticated.
															</span>
														</div>
													)}
												</div>
												<button type="button" className="btn uk-change" onClick={() => setAuthOpen(true)}>
													{activeKey || token ? 'Change keys' : 'Choose keys'}
												</button>
											</div>

											<button
												type="button"
												className={`btn pri send-btn${isLive ? ' live' : envMode === 'sandbox' ? ' sandbox' : ''}`}
												disabled={sending}
												onClick={requestSend}
											>
												{sending
													? 'Sending…'
													: isLive
														? 'Send with PRODUCTION keys'
														: envMode === 'sandbox'
															? 'Send with sandbox keys'
															: 'Send'}
											</button>
											{sendErr ? <div className="dferr">{sendErr}</div> : null}
										</Section>

										{response ? (
											<Section title="Response">
												<div className={`resp-accent ${statusTone(response.status)}`} />
												<div className="resp-status">
													<span className={`resp-code ${statusTone(response.status)}`}>
														{response.status}
													</span>
													<span className="resp-text">{response.statusText}</span>
													<span className="resp-time mono">{Math.round(response.duration)} ms</span>
													{response.requestId ? (
														<span className="resp-reqid mono" title="Simplici-Request-Id — what support asks for">
															{response.requestId}
														</span>
													) : null}
												</div>
												<details className="resp-headers">
													<summary>Headers ({Object.keys(response.headers).length})</summary>
													<table className="kv">
														<tbody>
															{Object.entries(response.headers).map(([k, v]) => (
																<tr key={k}>
																	<td>{k}</td>
																	<td className="mono">{v}</td>
																</tr>
															))}
														</tbody>
													</table>
												</details>
												{response.isJson ? (
													<JsonView value={response.json} />
												) : (
													<RawTextView text={response.text} />
												)}
											</Section>
										) : null}

										{/* WHAT TO DO ABOUT IT — Austin, 2026-09-24. A response panel that
										    shows only the refusal leaves a developer to work out for
										    themselves whether the fault is their request, their token, their
										    credential or the endpoint. This says which, in its own frame, and
										    where the fix is one action we can take for them it offers the
										    action rather than describing it. */}
										{response && response.status >= 400 ? (
											<Section title="How to fix this">
												<FixIt
													response={response}
													baseUrl={baseUrl}
													hasToken={!!token}
													onOpenCredentials={() => setAuthOpen(true)}
													onWidened={() => setToken(null)}
												/>
											</Section>
										) : null}
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			) : null}

			<Drawer open={authOpen} label="Credentials">
				<DrawerHead title="Credentials" onClose={() => setAuthOpen(false)}>
					<div className="chint">Base URL: {trimSlash(baseUrl)}</div>
				</DrawerHead>
				<AuthPanel
					baseUrl={baseUrl}
					token={token}
					onToken={setToken}
					onMode={setKeyMode}
					onActiveKey={setActiveKey}
					/* The drawer hides its children with CSS rather than unmounting
					   them, so the panel has to be told when it is actually on screen
					   — its history poll runs only then. */
					open={authOpen}
				/>
			</Drawer>

			<Confirm
				open={!!confirmOp}
				title="Real, billable call"
				body={
					<>
						<b>{confirmOp?.summary}</b> reaches the underlying screening/verification provider
						for real and may be billed. This is not a simulation — sending it now will run the
						same check a live applicant would trigger.
					</>
				}
				label="Send anyway"
				tone="danger"
				onConfirm={() => {
					const op = confirmOp;
					setConfirmOp(null);
					if (op) runSend(op);
				}}
				onClose={() => setConfirmOp(null)}
			/>
		</div>
	);
};

export default ApiConsole;
